import {
    AuditLog,
    AuditLogChangeType,
    AuditLogDiff,
} from "@common/types/auditLog";
import { MessageSentBy } from "@common/types/chat";
import { useMemo } from "react";
import { ChatTaskAuditLog } from "@components/auditLog/chat/ChatTaskAuditLog";
import { capitalize } from "@common/helpers/string";
import styles from "./AuditLogsMessage.module.scss";

interface AuditLogsMessageProps {
    content: string;
    actor: MessageSentBy | null;
}

type AuditLogDiffLike = Partial<AuditLogDiff>;

interface AuditLogsThreadPreviewProps {
    content: string;
}

const DEFAULT_AUDIT_LOG_PREVIEW = "Task was updated";
const CREATED_AUDIT_LOG_PREVIEW = "Task has been created";
const SUPPORTED_AUDIT_LOG_FIELD_NAMES: Record<string, string> = {
    title: "title",
    status: "status",
    assigneeId: "assignee",
    dueDate: "due date",
    priority: "priority",
    estimatedTime: "estimate",
    entities: "entities",
    financialDocuments: "financial documents",
    blockedByTasks: "blocked by tasks",
    blockingTasks: "blocking tasks",
    relatedTasks: "related tasks",
};

function isEmptyValue(value: unknown): boolean {
    if (value === null || value === undefined || value === "") {
        return true;
    }
    if (Array.isArray(value) && value.length === 0) {
        return true;
    }
    return false;
}

function formatFieldName(field: string): string | null {
    return SUPPORTED_AUDIT_LOG_FIELD_NAMES[field] ?? null;
}

function summarizeAuditLogMessage(content: string): string {
    try {
        const parsed = JSON.parse(content) as {
            changeType?: AuditLogChangeType;
            diff?: AuditLogDiffLike[];
        };
        if (parsed.changeType === AuditLogChangeType.CREATE) {
            return CREATED_AUDIT_LOG_PREVIEW;
        }

        const diffs = parsed.diff ?? [];
        if (diffs.length === 0) {
            return DEFAULT_AUDIT_LOG_PREVIEW;
        }

        const changeSummaries = diffs
            .filter(
                (
                    diff,
                ): diff is Required<Pick<AuditLogDiffLike, "field">> &
                    AuditLogDiffLike => Boolean(diff.field),
            )
            .map((diff) => {
                const fieldName = formatFieldName(diff.field);
                if (!fieldName) {
                    return null;
                }

                const wasEmpty = isEmptyValue(diff.before?.value);
                const isEmpty = isEmptyValue(diff.after?.value);
                let changeType = "changed";
                if (wasEmpty && !isEmpty) {
                    changeType = "added";
                } else if (!wasEmpty && isEmpty) {
                    changeType = "removed";
                }

                return `${fieldName} was ${changeType}`;
            })
            .filter((summary): summary is string => Boolean(summary));

        if (changeSummaries.length === 0) {
            return DEFAULT_AUDIT_LOG_PREVIEW;
        }

        return changeSummaries.join(", ");
    } catch {
        return DEFAULT_AUDIT_LOG_PREVIEW;
    }
}

export const AuditLogsThreadPreview = ({
    content,
}: AuditLogsThreadPreviewProps) => {
    const summary = useMemo(() => {
        const message = summarizeAuditLogMessage(content);
        return capitalize(message);
    }, [content]);
    return <>{summary}</>;
};

export const AuditLogsMessage = ({ content, actor }: AuditLogsMessageProps) => {
    const auditLogData = JSON.parse(content) as AuditLog;
    if (!auditLogData) {
        return null;
    }

    return (
        <div className={styles.chatTaskSystemMessage}>
            <ChatTaskAuditLog log={auditLogData} actor={actor} />
        </div>
    );
};
